import { del, get, http, patch, post } from './client'
import { nomDeDisposition } from '@/components/common/telechargement'
import type { FondVue } from '@/components/vues/fonds'

/**
 * Données GÉNÉRIQUES : vues (barre latérale), tableaux, champs, records.
 *
 * Contrat du backend `engine/vues`. Les valeurs d'un record sont indexées par la
 * CLÉ du champ ; un choix s'échange par la clé de son option, jamais par un
 * identifiant interne.
 */

export type Langue = 'fr' | 'es' | 'en'
export type Libelles = { fr: string } & Partial<Record<Exclude<Langue, 'fr'>, string>>

export type TypeChamp =
  | 'texte'
  | 'texte_long'
  | 'email'
  | 'telephone'
  | 'url'
  | 'nombre'
  | 'monnaie'
  | 'pourcentage'
  | 'note'
  | 'duree'
  | 'booleen'
  | 'date'
  | 'date_heure'
  | 'choix'
  | 'choix_multiple'
  | 'etiquettes'
  | 'utilisateur'
  | 'utilisateurs'
  | 'groupe'
  | 'groupes'
  | 'lien'
  | 'liens'
  | 'sous_tableau'
  | 'titre'
  | 'paragraphe'
  | 'alerte'
  | 'separateur'
  | 'bouton'
  | 'adresse'
  | 'document'
  | 'documents'
  | 'formule'
  | 'numero_auto'
  | 'cree_le'
  | 'modifie_le'
  | 'cree_par'
  | 'cle_fiche'

export type CouleurOption = 'neutre' | 'primaire' | 'succes' | 'attention' | 'danger'

export interface OptionChamp {
  id: string
  cle: string
  libelles: Libelles
  couleur: CouleurOption
  position: number
}

/** Réglages propres au type, tels que normalisés par le serveur. */
export interface ConfigChamp {
  longueur_max?: number
  /** Texte à forme fixe (code postal) : longueur minimale. */
  longueur_min?: number
  /** Texte à forme fixe : `chiffres` = chiffres seuls. Absent : tout caractère. */
  caracteres?: 'chiffres'
  decimales?: number
  min?: number
  max?: number
  devise?: string
  affichage?: 'hh:mm' | 'hh:mm:ss'
  max_elements?: number
  prefixe?: string
  chiffres?: number
  defaut?: unknown
  /** Adresse : seul pays accepté (ISO 3166-1 alpha-2). Absent : tout pays. */
  pays?: string
  /**
   * Adresse : clé d'API (son id, jamais son contenu) du fournisseur qui fait les
   * propositions. Absent : les fournisseurs publics, par pays.
   */
  cle_id?: string
  /** Téléphone : pays des numéros saisis sans indicatif, proposé à la saisie. */
  pays_defaut?: string
  /** La valeur se modifie dans la cellule du tableau, enregistrée à la volée (`edition-cellule.contrat.json`). */
  edition_cellule?: boolean
  /**
   * Forme du contrôle dans la fiche (`saisie-choix.contrat.json`) : « boutons », « liste » déroulante, ou
   * « statut » (choix seulement : pastille pleine largeur, menu avec recherche, dernier changement), ou « etapes »
   * (choix simple : parcours, les options dans l’ordre sont les étapes). Absent : selon le nombre d’options.
   */
  saisie?: 'boutons' | 'liste' | 'statut' | 'etapes'
  /** Parcours (`saisie: 'etapes'`) : clés des options qui sont des SORTIES (Annulé, Perdu), pas des étapes. */
  etapes_sorties?: string[]
  /** Formule : l'expression calculée par le serveur. */
  expression?: string
  /** Formule : type sous lequel son résultat est rangé, affiché, filtré. */
  resultat?: 'nombre' | 'monnaie' | 'pourcentage' | 'texte' | 'booleen' | 'date' | 'date_heure'
  /** Sous-tableau : champ `lien` (ou `liens`) de la CIBLE qui désigne la fiche ouverte. */
  /** Sous-tableau : « liees » (défaut), celles qui citent la fiche ; « toutes », un référentiel. */
  source?: 'liees' | 'toutes'
  champ_lien?: string
  /** Sous-tableau de référence : critères fixes et correspondances avec la fiche ouverte. */
  filtres?: ExpressionFiltre[]
  correspondances?: Array<{ champ: string; champ_fiche: string }>
  /** Sous-tableau : champs de la cible montrés en colonnes. Absent : son champ principal. */
  colonnes?: string[]
  /** Sous-tableau : `cle` ou `-cle` d'un champ de la cible. Absent : les plus récentes d'abord. */
  tri?: string
  /** Sous-tableau : fiches montrées d'office, « Voir plus » au-delà. */
  limite?: number
  /** Sous-tableau : « + Ajouter » crée une fiche de la cible déjà liée. */
  creation?: boolean
  /**
   * Sous-tableau : bouton « Sélectionner » — des fiches EXISTANTES de la
   * cible, proposées selon `filtres`, rattachées avec les `valeurs` fixes.
   */
  rattachement?: { filtres?: ExpressionFiltre[]; valeurs?: Record<string, ValeurChamp>; colonnes?: string[] }
  /** Lien(s) : fiches que le lien peut citer — la liste de choix est restreinte par le serveur. */
  choix?: { filtres: ExpressionFiltre[] }
  /** Groupe(s) : un membre d'au moins un groupe ne choisit qu'entre les siens (sans groupe : tous). */
  mes_groupes_seulement?: boolean
  /** Sous-tableau : où s'ouvre une fiche de la cible. Absent : `page`. */
  ouverture?: OuvertureFiche
  /** Sous-tableau : comment les fiches sont montrées. Absent : `liste`. */
  presentation?: PresentationSousTableau
  /** Sous-tableau : carte rôle → clé de champ de la cible (« le message, c'est `contenu` »). */
  roles?: Record<string, string>
  /** Mise en page (paragraphe, alerte) : texte enrichi par langue — `**gras**`, `*italique*`, liens, listes. */
  texte?: Partial<Libelles>
  /** Titre : `titre` ou `sous_titre` (un niveau plus bas). */
  niveau?: 'titre' | 'sous_titre'
  /** Alerte : ce qu'elle signifie — couleur, icône et nom lu en découlent. */
  ton?: TonAlerte
  /** Paragraphe, alerte, séparateur : le libellé est-il montré ? */
  libelle_visible?: boolean
  /** Paragraphe, alerte : `demie` se pose à côté d'un champ. Absent : `pleine`. */
  largeur?: 'pleine' | 'demie'
  /** Séparateur : une ligne, ou un espace qui aère sans rien tracer. */
  style?: 'ligne' | 'espace'
  /** Bouton : ce que fait un clic (`bouton.contrat.json`). */
  action?: ActionBouton
  /** Bouton « lien » : adresse ouverte dans un nouvel onglet. */
  url?: string
}

/** Actions d'un bouton — miroir de `ACTIONS_BOUTON` du serveur, figé par `bouton.contrat.json`. */
export type ActionBouton = 'lien' | 'automatisation'

/** Tons d'une alerte — miroir de `TONS_ALERTE` du serveur (`config.ts`). */
export type TonAlerte = 'info' | 'succes' | 'attention' | 'danger' | 'neutre'

/** Présentation d'un sous-tableau : un tableau de colonnes, ou une conversation. */
export type PresentationSousTableau = 'liste' | 'chat'

/** Où s'ouvre une fiche depuis un sous-tableau : sa page, ou par-dessus la fiche parente. */
export type OuvertureFiche = 'page' | 'modale' | 'tiroir'

export interface Champ {
  id: string
  cle: string
  type: TypeChamp
  libelles: Libelles
  aides: Partial<Libelles>
  config: ConfigChamp
  obligatoire: boolean
  estPrincipal: boolean
  cibleTableauId: string | null
  position: number
  options: OptionChamp[]
  /** Section de la fiche (clé) ; `null` = avant les sections. */
  section: string | null
  /**
   * Conditions d'AFFICHAGE : le champ n'est montré — et n'a de valeur — que si
   * toutes sont vraies. Le serveur les applique à l'écriture (champ masqué
   * vide, obligatoire seulement si affiché) ; l'écran les reflète.
   */
  conditions: ExpressionFiltre[]
  /**
   * Faux : le champ ne se montre pas dans la FICHE — ni dans sa section, ni
   * dans le panneau des valeurs calculées. Il reste colonne, filtre, tri et
   * valeur lue partout ailleurs.
   */
  dansFiche: boolean
}

/** Section de la fiche, dans l'ordre du tableau. */
export interface SectionFiche {
  cle: string
  libelles: Libelles
  /** Un emoji, affiché devant le titre de la section. Absent : aucun. */
  emoji?: string
}

export interface Tableau {
  id: string
  /** Non nulle : tableau géré par l'application (ex. « items »). */
  cle: string | null
  /** Préfixe des clés de ses fiches (« FA » dans « FA-152 »), unique dans l'organisation. */
  prefixe: string
  libelles: Libelles
  sections: SectionFiche[]
  champs: Champ[]
  /** Version de la structure lue : la « version attendue » d'une modification de structure. */
  version: number
}

/** Unité des périodes de date (« cette semaine », « 3 derniers mois »). */
export type UnitePeriode = 'jour' | 'semaine' | 'mois' | 'trimestre' | 'annee'

/**
 * Ce que vise une condition : un champ, ou UNE partie d'une valeur composée
 * (le code postal d'une adresse), jugée alors comme un texte libre.
 */
export interface CibleFiltre {
  champ: string
  partie?: string
}

/**
 * Condition de filtre — contrat de `engine/vues/filtres.ts` côté serveur, qui
 * en fixe le sens (casse, champ vide, jour dans le fuseau de l'organisation).
 * Appliquée par le SERVEUR : l'écran ne filtre rien lui-même.
 */
export type ConditionFiltre = CibleFiltre &
  (
    | { operateur: 'vide' | 'non_vide' }
    | { operateur: 'egal' | 'different'; valeur: string | number | boolean }
    | { operateur: 'parmi' | 'hors' | 'tous'; valeurs: Array<string | number> }
    | { operateur: 'contient' | 'ne_contient_pas' | 'commence_par' | 'finit_par'; valeur: string }
    | { operateur: 'superieur' | 'superieur_egal' | 'inferieur' | 'inferieur_egal'; valeur: string | number }
    | { operateur: 'entre'; min: string | number; max: string | number }
    | { operateur: 'periode'; unite: UnitePeriode; decalage: number }
    | { operateur: 'derniers' | 'prochains' | 'il_y_a_plus_de' | 'dans_plus_de'; nombre: number; unite: UnitePeriode }
  )

export type Operateur = ConditionFiltre['operateur']

/** Groupe de conditions combinées par ET ou par OU. */
export interface GroupeFiltre {
  logique: 'et' | 'ou'
  conditions: ExpressionFiltre[]
}

export type ExpressionFiltre = ConditionFiltre | GroupeFiltre

/** Valeur dynamique des champs de compte : le compte qui lit (« Mes leads »). */
export const MOI = '@moi'

export interface ReglagesVue {
  /** Filtres VERROUILLÉS, combinés par ET. */
  filtres?: ExpressionFiltre[]
  /** Tri par défaut : `cle` ou `-cle`. */
  tri?: string
  /** Colonnes affichées, dans l'ordre. Absent : toutes. */
  colonnes?: string[]
  /**
   * Champs à choix proposés en filtre rapide, dans l'ordre. Absent : un par
   * colonne à choix affichée ; `[]` : aucun.
   */
  filtres_rapides?: string[]
  /**
   * Volet latéral de filtres, à côté de la table : options d'un champ à choix
   * unique (`champs`, le premier s'ouvre ; absent : tous les champs à choix
   * unique) avec leur nombre de fiches. `axe` : celui ouvert par défaut
   * (absent : le premier). `cote` : à gauche (défaut) ou à droite de la
   * table. `ouvert` : état à la première visite (absent : ouvert). Absent :
   * pas de volet.
   */
  volet?: { champs?: string[]; axe?: string; cote?: 'gauche' | 'droite'; ouvert?: boolean }
  /**
   * Façons de montrer les fiches de la vue, le premier s'ouvrant par défaut.
   * Absent : la table seule. Validés par le serveur (`reglages.ts`).
   */
  affichages?: AffichageVue[]
  /**
   * Teinte pastel derrière l'écran de la vue, parmi une liste fermée
   * (`components/vues/fonds.ts`). Absent : aucune.
   */
  fond?: FondVue
}

/**
 * Fonctions d'un total de colonne (miroir de `mesures.ts`, backend). Les
 * numériques ne valent que sur un champ numérique ; le serveur le vérifie à
 * l'enregistrement de la vue.
 */
export const FONCTIONS_TOTAL = ['somme', 'moyenne', 'min', 'max', 'remplis', 'vides'] as const
export type FonctionTotal = (typeof FONCTIONS_TOTAL)[number]

export type AffichageVue =
  /** `totaux` : fonction montrée au pied de chaque colonne désignée, sur toutes les fiches de la vue. */
  | { type: 'table'; totaux?: Record<string, FonctionTotal> }
  /** Une colonne par option du champ à choix unique `champ`. */
  | { type: 'kanban'; champ: string; cartes?: string[] }
  /** Grille du mois ; une fiche s'étale de `debut` à `fin`. */
  | { type: 'calendrier'; debut: string; fin?: string; echelle?: 'mois' | 'semaine' | 'jour' }
  /**
   * Fiches placées d'après leur champ adresse `champ` ; `cible` : zone
   * d'ouverture, un pays (« FR ») ou un pays et une ville (« FR.Paris »).
   */
  | { type: 'carte'; champ: string; cible?: string }
  /** Une carte par fiche, coiffée de la photo du champ document `image`. */
  | { type: 'galerie'; image: string; cartes?: string[] }
  /** La table, ses lignes regroupées par option du champ à choix `champ` ; `totaux` comme la table. */
  | { type: 'groupes'; champ: string; totaux?: Partial<Record<string, FonctionTotal>> }

export type TypeAffichage = AffichageVue['type']

// ─── Widgets d'un tableau de bord (miroir de `engine/vues/widgets.ts`, `api/tableauxDeBord.ts`) ───

export type MesureWidget = { fonction: 'compte' } | { fonction: FonctionTotal; champ: string }

export interface AxeWidget {
  champ: string
  /** Requise pour une date, refusée sinon (le serveur le vérifie à l'enregistrement). */
  granularite?: Granularite
}

export type SensValeurs = 'haut' | 'bas'

interface WidgetCommun {
  /** Identifiant stable, posé par qui construit. */
  id: string
  /** Vue MESURÉE — de n'importe quel tableau. */
  vueId: string
  libelles: Libelles
  /** Colonnes d'une grille de quatre. */
  largeur: 1 | 2 | 3 | 4
  mesure: MesureWidget
}

/** Un chiffre : la mesure sur toutes les fiches de la vue. */
export interface WidgetIndicateur extends WidgetCommun {
  type: 'indicateur'
}

/** Un tableau croisé : la mesure par lignes, et par colonnes si `colonnes` est donné. */
export interface WidgetTableau extends WidgetCommun {
  type: 'tableau'
  lignes: AxeWidget
  colonnes?: AxeWidget
  sens?: SensValeurs
  podium?: boolean
}

/** Des barres : la mesure par groupe (une date en colonnes chronologiques). */
export interface WidgetBarres extends WidgetCommun {
  type: 'barres'
  lignes: AxeWidget
}

export type Widget = WidgetIndicateur | WidgetTableau | WidgetBarres
export type TypeWidget = Widget['type']

export interface Vue {
  id: string
  tableauId: string
  /**
   * Conteneur dont la vue est un ONGLET, ou `null` : la vue est alors une
   * entrée de la barre latérale. Sa `position` ordonne les onglets.
   */
  parentId: string | null
  libelles: Libelles
  /** Clé du catalogue de pictogrammes (`components/vues/icones.ts`). */
  icone: string
  position: number
  reglages: ReglagesVue
}

/**
 * Valeur COMPOSÉE : un objet de parties texte (adresse : `{ rue, code_postal,
 * ville, pays… }`). Une partie absente est une clé absente, jamais `''`.
 */
export type ValeurComposee = Readonly<Record<string, string>>

export type ValeurChamp = string | number | boolean | string[] | ValeurComposee | null

export interface RecordVue {
  id: string
  tableauId: string
  /** Clé de la fiche (« FA-152 ») : préfixe du tableau et numéro de création, jamais réattribué. */
  cle: string
  /** Partie chiffrée de la clé. */
  numero: number
  /** TOUS les champs : `null` ou `[]` quand rien n'est renseigné. */
  valeurs: Record<string, ValeurChamp>
  createdAt: string
  updatedAt: string
  /**
   * Version EXACTE (`updated_at` à la microseconde, chiffres UTC de largeur
   * fixe) : comparable comme une chaîne, strictement croissante pour une fiche.
   * `updatedAt`, à la milliseconde, ne départage pas deux écritures proches.
   */
  version: string
  createdBy: string | null
  /**
   * Présent quand une formule de la fiche attend son recalcul, parce qu'une
   * fiche qu'elle LIT vient de changer : la valeur affichée peut être
   * dépassée, la nouvelle arrive dans les secondes qui suivent.
   */
  recalculEnAttente?: true
}

/** Ce que l'API montre d'un document joint : jamais son emplacement. */
export interface DocumentInfo {
  id: string
  nom: string
  /** Type MIME reconnu sur le CONTENU par le serveur. */
  type: string
  /** Octets. */
  taille: number
}

/** Noms des comptes, libellés des records et documents cités, rendus à côté des records. */
export interface References {
  utilisateurs: Record<string, { nom: string; email: string; actif: boolean }>
  records: Record<string, { tableauId: string; libelle: string | null; cle: string }>
  documents: Record<string, DocumentInfo>
  /** Nom des groupes cités (champs `groupe`, `groupes`). */
  groupes: Record<string, { nom: string }>
}

/** Références vides : une seule écriture, pour qu'une clé ajoutée ne manque nulle part. */
export function referencesVides(): References {
  return { utilisateurs: {}, records: {}, documents: {}, groupes: {} }
}

export interface PageRecords {
  records: RecordVue[]
  total: number
  limit: number
  offset: number
  references: References
}

export interface RequeteRecords {
  recherche?: string
  /** `cle` ou `-cle` (décroissant). */
  tri?: string
  /** `{ statut: 'signe' }` → `?filtre.statut=signe`. `'_vide'` = sans valeur. */
  filtres?: Record<string, string>
  /** Filtres AVANCÉS (groupes, opérateurs, périodes) → `?filtres=<json>`. */
  expressions?: ExpressionFiltre[]
  limit?: number
  offset?: number
}

export function listerVues(): Promise<{ vues: Vue[] }> {
  return get<{ vues: Vue[] }>('/vues')
}

/**
 * CONTENEUR de vues : une entrée de la barre latérale sans tableau ni
 * réglages, qui affiche ses sous-vues en onglets.
 */
export interface Conteneur {
  id: string
  libelles: Libelles
  icone: string
  position: number
}

export function listerConteneurs(): Promise<{ conteneurs: Conteneur[] }> {
  return get<{ conteneurs: Conteneur[] }>('/vues-conteneurs')
}

export function lireVue(id: string, signal?: AbortSignal): Promise<{ vue: Vue; tableau: Tableau }> {
  return get<{ vue: Vue; tableau: Tableau }>(`/vues/${id}`, { signal })
}

/** Définition d'un tableau (ex. la cible d'un champ de lien). */
export function lireTableauDe(id: string): Promise<Tableau> {
  return get<Tableau>(`/tableaux/${id}`)
}

function parametresListe(requete: RequeteRecords): Record<string, string | number> {
  const params: Record<string, string | number> = {}
  if (requete.recherche) params.recherche = requete.recherche
  if (requete.tri) params.tri = requete.tri
  if (requete.limit !== undefined) params.limit = requete.limit
  if (requete.offset !== undefined) params.offset = requete.offset
  for (const [cle, valeur] of Object.entries(requete.filtres ?? {})) {
    params[`filtre.${cle}`] = valeur
  }
  if (requete.expressions && requete.expressions.length > 0) {
    params.filtres = JSON.stringify(requete.expressions)
  }
  return params
}

/** Position d'une fiche sur la carte (`GET /vues/:id/positions`). */
export interface PositionFiche {
  id: string
  /** Champ principal ; `null` s'il est vide. */
  nom: string | null
  lat: number
  lon: number
  /** Placée au centre de sa ville : sa rue n'a pas été reconnue. */
  approximative: boolean
  adresse: ValeurComposee
}

export interface PositionsVue {
  positions: PositionFiche[]
  /** Trop de fiches placées : seules les premières sont rendues. */
  tronque: boolean
  /** Fiches de la vue sans position : total exact, et les premières nommées. */
  sansPosition: { total: number; fiches: Array<{ id: string; nom: string | null }> }
}

/** Critères de la carte : ceux de la liste, sans tri ni pagination. */
export type RequetePositions = Pick<RequeteRecords, 'recherche' | 'filtres' | 'expressions'>

/**
 * Positions des fiches d'une VUE (affichage Carte) : les mêmes fiches que la
 * liste, sous les mêmes critères, toutes d'un coup. Le champ adresse est celui
 * que la vue déclare — le serveur le lit dans ses réglages.
 */
export function listerPositionsVue(vueId: string, requete: RequetePositions = {}, signal?: AbortSignal): Promise<PositionsVue> {
  return get<PositionsVue>(`/vues/${vueId}/positions`, { params: parametresListe(requete), signal })
}

/**
 * Fiches d'une VUE, déjà filtrées par le serveur selon ses filtres verrouillés.
 * Les filtres passés ici ne peuvent qu'AFFINER ; le tri par défaut est celui
 * de la vue.
 */
export function listerRecordsVue(
  vueId: string,
  requete: RequeteRecords = {},
  signal?: AbortSignal,
): Promise<PageRecords> {
  return get<PageRecords>(`/vues/${vueId}/records`, { params: parametresListe(requete), signal })
}

export type Mesure = { fonction: 'compte' } | { fonction: FonctionTotal; champ: string }
export type Granularite = 'jour' | 'semaine' | 'mois' | 'trimestre' | 'annee'

export interface DemandeAgregats {
  mesures: Mesure[]
  /** Regroupement ; les LIGNES d'un croisement. */
  regrouper?: string
  /** Requise pour regrouper par une date ; refusée sinon. */
  granularite?: Granularite
  /** Second axe : les COLONNES d'un croisement (exige `regrouper`). */
  croiser?: string
  granulariteCroisement?: Granularite
}

/**
 * Valeur d'une mesure : entier pour un compte (`compte`, `remplis`, `vides`),
 * TEXTE décimal exact pour les autres (une somme peut dépasser ce qu'un
 * `number` relit exactement), `null` sans aucune valeur.
 */
export type ValeurMesure = number | string | null

export interface ResultatAgregat {
  compte: number
  /** Dans l'ordre des mesures demandées. */
  mesures: ValeurMesure[]
}

export interface GroupeAgregat extends ResultatAgregat {
  /** Clé d'option, identifiant, booléen, texte ou premier jour de la période ; `null` : sans valeur. */
  valeur: string | boolean | null
  /** Nom du compte ou du groupe désigné ; `null` sinon. */
  libelle: string | null
}

/** Une case d'un croisement : les fiches qui ont cette ligne ET cette colonne. */
export interface CelluleAgregat extends ResultatAgregat {
  ligne: string | boolean | null
  colonne: string | boolean | null
}

export interface Agregats {
  total: ResultatAgregat
  /** Groupes du regroupement (les lignes d'un croisement) ; `null` sans regroupement demandé. */
  groupes: GroupeAgregat[] | null
  /** Trop de groupes : seuls les premiers sont rendus (le total reste exact). Jamais vrai pour un croisement : refusé. */
  groupesTronques: boolean
  /** Colonnes d'un croisement, avec leur total ; `null` sans croisement. */
  colonnes: GroupeAgregat[] | null
  /** Cases NON VIDES d'un croisement ; une case absente n'a aucune fiche. */
  cellules: CelluleAgregat[] | null
}

/** Critères des agrégats : ceux de la liste, sans tri ni pagination. */
export type RequeteAgregats = Pick<RequeteRecords, 'recherche' | 'filtres' | 'expressions'>

/**
 * Agrégats des fiches d'une VUE (`GET /vues/:id/agregats`) : calculés par le
 * serveur sur les mêmes fiches que la liste, sous les mêmes critères — jamais
 * sur la page chargée.
 */
export function agregerVue(
  vueId: string,
  demande: DemandeAgregats,
  requete: RequeteAgregats = {},
  signal?: AbortSignal,
): Promise<Agregats> {
  const params: Record<string, string | number> = { ...parametresListe(requete), mesures: JSON.stringify(demande.mesures) }
  if (demande.regrouper !== undefined) params.regrouper = demande.regrouper
  if (demande.granularite !== undefined) params.granularite = demande.granularite
  if (demande.croiser !== undefined) params.croiser = demande.croiser
  if (demande.granulariteCroisement !== undefined) params.granulariteCroisement = demande.granulariteCroisement
  return get<Agregats>(`/vues/${vueId}/agregats`, { params, signal })
}

/** Crée une fiche DEPUIS une vue : les valeurs verrouillées sont imposées par le serveur. */
export function creerRecordVue(vueId: string, valeurs: Record<string, ValeurChamp>): Promise<RecordVue> {
  return post<RecordVue>(`/vues/${vueId}/records`, { valeurs })
}

/**
 * Création DIRECTEMENT dans un tableau, sans vue : un tableau existe sans vue
 * (sous-tableau, cible de lien). Aucune valeur n'est imposée — c'est la vue
 * qui en impose (ses filtres verrouillés), et il n'y en a pas.
 */
export function creerRecord(tableauId: string, valeurs: Record<string, ValeurChamp>): Promise<RecordVue> {
  return post<RecordVue>(`/tableaux/${tableauId}/records`, { valeurs })
}

/**
 * Fiches d'un TABLEAU. `choix` : l'identifiant d'un champ lien qui vise ce
 * tableau — sa liste de choix, restreinte par le serveur selon la règle
 * `config.choix` du champ (jamais envoyée d'ici).
 */
export function listerRecords(
  tableauId: string,
  requete: RequeteRecords & { choix?: string } = {},
  signal?: AbortSignal,
): Promise<PageRecords> {
  const params = parametresListe(requete)
  if (requete.choix) params.choix = requete.choix
  return get<PageRecords>(`/tableaux/${tableauId}/records`, { params, signal })
}

export function lireRecord(
  tableauId: string,
  recordId: string,
): Promise<{ record: RecordVue; references: References }> {
  return get(`/tableaux/${tableauId}/records/${recordId}`)
}

// ─── Historique d'une fiche ──────────────────────────────────────────────────

/** Par où une écriture est arrivée (contrat de `engine/vues/historique`). */
export type CanalRevision = 'application' | 'masse' | 'passerelle' | 'mcp' | 'automatisation' | 'entrant'

/**
 * Pourquoi des valeurs ont changé SANS que personne ne modifie la fiche : une
 * fiche liée ou un groupe supprimés, un changement de structure du tableau,
 * la restauration d'une de ses versions.
 */
export type CauseRevision = 'fiche_liee_supprimee' | 'groupe_supprime' | 'structure' | 'restauration'

/**
 * Le champ TEL QU'IL ÉTAIT au moment de l'écriture : il a pu être renommé,
 * perdre une option, ou disparaître depuis. Seules les options citées par
 * le changement sont figées.
 */
export interface ChampFige {
  id: string
  cle: string
  type: TypeChamp
  libelles: Libelles
  config: ConfigChamp
  cibleTableauId: string | null
  options: Array<Pick<OptionChamp, 'cle' | 'libelles' | 'couleur'>>
}

export interface ChangementRevision {
  champ: ChampFige
  avant: ValeurChamp
  apres: ValeurChamp
}

export interface Revision {
  /** Identifiant et curseur ; une chaîne, parce qu'un bigint dépasse `number`. */
  id: string
  nature: 'creation' | 'modification'
  canal: CanalRevision
  /** `null` : automatisation, support (`exploitation`) ou compte supprimé. */
  auteurId: string | null
  exploitation: boolean
  /** `null` : une saisie. */
  cause: CauseRevision | null
  /** Version de structure qui l'a causée (causes `structure` et `restauration`). */
  versionStructure: number | null
  creeLe: string
  changements: ChangementRevision[]
}

export interface PageHistorique {
  revisions: Revision[]
  /** Révisions qui répondent au filtre, toutes pages confondues. */
  total: number
  /** Curseur de la page suivante ; `null` : c'était la dernière. */
  suivant: string | null
  references: References
}

export interface RequeteHistorique {
  /** Curseur : `suivant` de la page précédente. */
  avant?: string
  limite?: number
  /** Seulement les révisions qui ont changé ce champ (identifiant). */
  champ?: string
}

export function lireHistorique(
  tableauId: string,
  recordId: string,
  requete: RequeteHistorique = {},
  signal?: AbortSignal,
): Promise<PageHistorique> {
  const params: Record<string, string> = {}
  if (requete.avant !== undefined) params.avant = requete.avant
  if (requete.limite !== undefined) params.limite = String(requete.limite)
  if (requete.champ !== undefined) params.champ = requete.champ
  return get(`/tableaux/${tableauId}/records/${recordId}/historique`, { params, ...(signal ? { signal } : {}) })
}

/**
 * Dépose un fichier pour un champ `document`. La fiche le citera ensuite par
 * son identifiant, à son enregistrement — un dépôt jamais rattaché est
 * effacé par le serveur au bout d'un jour.
 *
 * Corps BRUT (`application/octet-stream`) : le serveur l'écrit sur disque au
 * fil de l'eau. Le nom voyage encodé dans un en-tête, qui ne transporte pas
 * d'UTF-8 de façon fiable.
 */
export function deposerDocument(
  tableauId: string,
  cle: string,
  fichier: File,
  progression?: (fraction: number) => void,
): Promise<DocumentInfo> {
  return post<DocumentInfo>(`/tableaux/${tableauId}/champs/${encodeURIComponent(cle)}/documents`, fichier, {
    headers: { 'Content-Type': 'application/octet-stream', 'X-Nom-Fichier': encodeURIComponent(fichier.name) },
    onUploadProgress: (e) => {
      if (progression && e.total) progression(Math.min(1, e.loaded / e.total))
    },
  })
}

/** Contenu d'un document CITÉ par la fiche `recordId` (404 sinon, ou hors périmètre). */
export function telechargerDocument(tableauId: string, recordId: string, documentId: string): Promise<Blob> {
  return get<Blob>(`/tableaux/${tableauId}/records/${recordId}/documents/${documentId}`, { responseType: 'blob' })
}

/**
 * La fiche en PDF, générée par le serveur avec les mêmes droits que sa
 * lecture (404 sinon, ou hors périmètre), dans la langue de l'écran. Le nom
 * du fichier est celui que le serveur donne (`Content-Disposition`) : une
 * seule règle de nommage, côté serveur.
 */
export async function telechargerFichePdf(tableauId: string, recordId: string, langue: Langue): Promise<{ contenu: Blob; nom: string }> {
  // Une réponse d'erreur en Blob est lue par l'intercepteur du client (`lireCorpsBlob`) : l'`ApiError` reçue
  // ici porte déjà le message du serveur.
  const reponse = await http.get<Blob>(`/tableaux/${tableauId}/records/${recordId}/pdf`, { params: { langue }, responseType: 'blob' })
  const nom = nomDeDisposition(reponse.headers['content-disposition'] as string | undefined)
  // Sans nom lisible (proxy qui retire l'en-tête) : un nom sûr, jamais un
  // téléchargement sans extension que le poste ne saurait ouvrir.
  return { contenu: reponse.data, nom: nom ?? 'fiche.pdf' }
}

/**
 * Mise à jour PARTIELLE : seules les clés fournies sont modifiées.
 *
 * `attendues` : pour chaque champ, la valeur que l'écran avait sous les yeux.
 * Si l'un de ces champs a changé depuis, le serveur refuse tout en 409
 * `version_conflict` (`fields` nomme les champs) — jamais d'écrasement à
 * l'aveugle. Absent : la dernière écriture gagne.
 */
/**
 * Aperçu des formules d'une saisie NON enregistrée : le serveur les calcule
 * avec le moteur de l'enregistrement, et n'écrit rien. `recordId` nul : fiche
 * à créer. `ignorees` : clés envoyées qu'il n'a pas retenues (valeur invalide,
 * champ non modifiable, lien hors de portée) — l'aperçu ne les reflète pas.
 */
export interface ApercuFormules {
  formules: Record<string, ValeurChamp>
  ignorees: string[]
}

export function apercuFormules(
  tableauId: string,
  recordId: string | null,
  valeurs: Record<string, ValeurChamp>,
  vueId?: string,
): Promise<ApercuFormules> {
  return post<ApercuFormules>(`/tableaux/${tableauId}/apercu-formules`, vueId === undefined ? { recordId, valeurs } : { recordId, vueId, valeurs })
}

export function modifierRecord(
  tableauId: string,
  recordId: string,
  valeurs: Record<string, ValeurChamp>,
  attendues?: Record<string, ValeurChamp>,
): Promise<RecordVue> {
  return patch<RecordVue>(
    `/tableaux/${tableauId}/records/${recordId}`,
    attendues === undefined ? { valeurs } : { valeurs, attendues },
  )
}

/**
 * Plafond d'une modification de masse — `MASSE_MAX` du serveur. Plafond et
 * forme de la réponse sont figés des deux côtés par
 * `backend/js/src/engine/vues/masse.contrat.json`.
 */
export const MASSE_MAX = 500

/** Une fiche non modifiée par une modification de masse : le refus qu'aurait reçu un PATCH seul. */
export interface EchecMasse {
  id: string
  statut: number
  code: string
  error: string
  fields?: Record<string, string>
}

export interface BilanMasse {
  /** Fiches modifiées, telles qu'écrites, dans l'ordre envoyé. */
  modifies: RecordVue[]
  echecs: EchecMasse[]
  /** Comptes et fiches cités par les fiches modifiées : rien à relire pour les nommer. */
  references: References
}

/**
 * Les MÊMES valeurs écrites sur plusieurs fiches, en une requête. Chaque fiche
 * réussit ou échoue seule : la réponse dit lesquelles, et pourquoi.
 */
export function modifierRecordsEnMasse(
  tableauId: string,
  ids: string[],
  valeurs: Record<string, ValeurChamp>,
): Promise<BilanMasse> {
  return patch<BilanMasse>(`/tableaux/${tableauId}/records`, { ids, valeurs })
}

/** Bilan d'une suppression de masse — forme figée par `masse.contrat.json`. */
export interface BilanSuppression {
  /** Fiches supprimées, dans l'ordre envoyé. */
  supprimes: string[]
  echecs: EchecMasse[]
}

/**
 * Supprime une sélection en UNE requête (`MASSE_MAX` fiches au plus). Chaque
 * fiche réussit ou échoue seule : la réponse dit lesquelles, et pourquoi.
 */
export function supprimerRecordsEnMasse(tableauId: string, ids: string[]): Promise<BilanSuppression> {
  return post<BilanSuppression>(`/tableaux/${tableauId}/records/suppression`, { ids })
}

/**
 * Clic sur un bouton « automatisation » : le serveur met en file les
 * automatisations qui l'écoutent et dit combien sont parties (`lancees`) et
 * combien ont échoué d'emblée, configuration devenue invalide (`echecs`).
 * Tout à 0 : aucune n'écoute ce bouton, ou aucune condition n'est remplie.
 */
export interface BilanClic {
  lancees: number
  echecs: number
}

export function cliquerBouton(tableauId: string, recordId: string, cle: string): Promise<BilanClic> {
  return post<BilanClic>(`/tableaux/${tableauId}/records/${recordId}/boutons/${cle}`)
}

/** Bilan d'un rattachement : comme une modification de masse, chaque fiche réussit ou échoue seule. */
export interface BilanRattachement {
  /** Fiches rattachées, telles qu'écrites, dans l'ordre envoyé. */
  rattaches: RecordVue[]
  echecs: EchecMasse[]
  references: References
}

/**
 * Rattache des fiches EXISTANTES de la cible du sous-tableau `champ` à la
 * fiche ouverte. La règle (fiches proposées, valeurs écrites) vient de la
 * structure : le serveur la rejuge sous le verrou de chaque fiche.
 */
export function rattacherRecords(tableauId: string, recordId: string, champ: string, ids: string[]): Promise<BilanRattachement> {
  return post<BilanRattachement>(`/tableaux/${tableauId}/records/${recordId}/rattacher`, { champ, ids })
}

/**
 * Range une fiche à la main dans une vue sans tri (ordre partagé par toute
 * l'équipe) : entre `dessus` et `dessous`, ses voisines APRÈS le dépôt dans
 * le groupe où on la lâche (`null` : en tête, ou en fin). 409 si la vue est triée.
 */
export function rangerFiche(vueId: string, recordId: string, dessus: string | null, dessous: string | null): Promise<void> {
  return post<void>(`/vues/${vueId}/ordre`, { recordId, dessus, dessous })
}

export function supprimerRecord(tableauId: string, recordId: string): Promise<void> {
  return del<void>(`/tableaux/${tableauId}/records/${recordId}`)
}
